import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Stage, Funnel } from '../types/funnels';
import { API_BASE_URL } from '../environment';

interface UseFunnelsReturn {
  funnels: Funnel[];
  loading: boolean;
  error: string | null;
  selectedFunnel: Funnel | null;
  setSelectedFunnel: React.Dispatch<React.SetStateAction<Funnel | null>>;
  refetchFunnels: () => Promise<void>; // Nova função para recarregar os funis
}

export const useFunnels = (): UseFunnelsReturn => {
  const [funnels, setFunnels] = useState<Funnel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFunnel, setSelectedFunnel] = useState<Funnel | null>(null);

  // Função de fetch movida para fora do useEffect e transformada em useCallback
  const fetchFunnels = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error('Token não encontrado');
      }
      
      const response = await axios.get(`${API_BASE_URL}/funis/read.php`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });

      if (response.data.success) {
        setFunnels(response.data.data);
        if (response.data.data.length > 0 && !selectedFunnel) {
          setSelectedFunnel(response.data.data[0]);
        }
      } else {
        throw new Error(response.data.message || 'Erro ao carregar os funis');
      }
    } catch (err) {
      console.error('Erro ao carregar funis:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar os funis';
      setError(errorMessage);
      throw err; // Propaga o erro para tratamento no componente
    }
  }, [selectedFunnel]);

  // Nova função de refetch que será exposta para o componente
  const refetchFunnels = useCallback(async () => {
    try {
      setLoading(true);
      setError(null); // Limpa erros anteriores
      await fetchFunnels();
    } finally {
      setLoading(false);
    }
  }, [fetchFunnels]);

  // useEffect usando a nova função fetchFunnels
  useEffect(() => {
    refetchFunnels();
  }, [refetchFunnels]);

  return {
    funnels,
    loading,
    error,
    selectedFunnel,
    setSelectedFunnel,
    refetchFunnels // Expõe a função de refetch
  };
};